import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { getAvailableVehicles, bookLimousine, calculateLimousineFare } from '../../services/limousineService';
import { LinearGradient } from 'expo-linear-gradient';

const BookLimousineScreen = ({ navigation, route }) => {
  const { pickup, dropoff, pickupCoords, dropoffCoords, distance } = route.params || {};

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  
  // Scheduling
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [scheduledTime, setScheduledTime] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  
  // Amenities
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response = await getAvailableVehicles();
      if (response.success) {
        setVehicles(response.data);
        if (response.data.length > 0) {
          setSelectedVehicle(response.data[0]);
        }
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const toggleAmenity = (amenity) => {
    if (selectedAmenities.includes(amenity)) {
      setSelectedAmenities(selectedAmenities.filter(a => a !== amenity));
    } else {
      setSelectedAmenities([...selectedAmenities, amenity]);
    }
  };

  const handleBooking = async () => {
    if (!selectedVehicle) {
      Alert.alert('Error', 'Please select a vehicle');
      return;
    }

    if (!pickup || !dropoff) {
      Alert.alert('Error', 'Please provide pickup and dropoff locations');
      return;
    }

    try {
      setBooking(true);
      
      const estimatedFare = calculateLimousineFare(selectedVehicle.type, distance || 10);
      
      const bookingData = {
        pickupLocation: pickup,
        dropoffLocation: dropoff,
        pickupLat: pickupCoords?.latitude,
        pickupLng: pickupCoords?.longitude,
        dropoffLat: dropoffCoords?.latitude,
        dropoffLng: dropoffCoords?.longitude,
        vehicleType: selectedVehicle.type,
        scheduledDate: scheduledDate.toISOString(),
        scheduledTime: scheduledTime.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        amenities: selectedAmenities,
        estimatedFare,
        notes
      };

      const response = await bookLimousine(bookingData);
      
      if (response.success) {
        Alert.alert(
          'Success',
          'Your limousine has been booked successfully!',
          [
            {
              text: 'OK',
              onPress: () => navigation.goBack()
            }
          ]
        );
      }
    } catch (error) {
      Alert.alert('Error', error.response?.data?.message || 'Failed to book limousine');
    } finally {
      setBooking(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEstimatedFare = () => {
    if (!selectedVehicle) return 0;
    return calculateLimousineFare(selectedVehicle.type, distance || 10);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#D4AF37" />
        <Text style={styles.loadingText}>Loading vehicles...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={['#0a0a0a', '#1a1a2e', '#16213e']}
        style={styles.backgroundGradient}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Book Limousine</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Location Summary */}
          <View style={styles.locationCard}>
            <View style={styles.locationRow}>
              <Ionicons name="location" size={20} color="#D4AF37" />
              <Text style={styles.locationText} numberOfLines={1}>{pickup || 'Pickup location'}</Text>
            </View>
            <View style={[styles.locationRow, { marginTop: 12 }]}>
              <Ionicons name="flag" size={20} color="#4CAF50" />
              <Text style={styles.locationText} numberOfLines={1}>{dropoff || 'Dropoff location'}</Text>
            </View>
          </View>

          {/* Vehicle Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Select Vehicle</Text>
            {vehicles.map((vehicle) => (
              <TouchableOpacity
                key={vehicle.type}
                style={[
                  styles.vehicleCard,
                  selectedVehicle?.type === vehicle.type && styles.vehicleCardSelected
                ]}
                onPress={() => setSelectedVehicle(vehicle)}
              >
                <LinearGradient
                  colors={selectedVehicle?.type === vehicle.type 
                    ? ['#D4AF37', '#B8860B'] 
                    : ['#1e1e1e', '#2d2d2d']}
                  style={styles.vehicleGradient}
                >
                  <View style={styles.vehicleHeader}>
                    <Ionicons 
                      name="car-sport" 
                      size={32} 
                      color={selectedVehicle?.type === vehicle.type ? '#000' : '#D4AF37'} 
                    />
                    <View style={styles.vehicleInfo}>
                      <Text style={[
                        styles.vehicleName,
                        selectedVehicle?.type === vehicle.type && styles.vehicleNameSelected
                      ]}>
                        {vehicle.name}
                      </Text>
                      <Text style={[
                        styles.vehicleDescription,
                        selectedVehicle?.type === vehicle.type && styles.vehicleDescriptionSelected
                      ]}>
                        {vehicle.description}
                      </Text>
                    </View>
                    <View style={styles.vehiclePrice}>
                      <Text style={[
                        styles.priceText,
                        selectedVehicle?.type === vehicle.type && styles.priceTextSelected
                      ]}>
                        ${vehicle.basePrice}
                      </Text>
                      <Text style={[
                        styles.priceLabel,
                        selectedVehicle?.type === vehicle.type && styles.priceLabelSelected
                      ]}>
                        base
                      </Text>
                    </View>
                  </View>
                  
                  <View style={styles.vehicleDetails}>
                    <View style={styles.detailItem}>
                      <Ionicons 
                        name="people" 
                        size={16} 
                        color={selectedVehicle?.type === vehicle.type ? '#000' : '#888'} 
                      />
                      <Text style={[
                        styles.detailText,
                        selectedVehicle?.type === vehicle.type && styles.detailTextSelected
                      ]}>
                        {vehicle.capacity} passengers
                      </Text>
                    </View>
                    <View style={styles.detailItem}>
                      <Ionicons 
                        name="cash-outline" 
                        size={16} 
                        color={selectedVehicle?.type === vehicle.type ? '#000' : '#888'} 
                      />
                      <Text style={[
                        styles.detailText,
                        selectedVehicle?.type === vehicle.type && styles.detailTextSelected
                      ]}>
                        ${vehicle.pricePerMile}/mile
                      </Text>
                    </View>
                  </View>

                  {selectedVehicle?.type === vehicle.type && (
                    <View style={styles.amenitiesPreview}>
                      <Text style={styles.amenitiesTitle}>Included Amenities:</Text>
                      <View style={styles.amenitiesRow}>
                        {vehicle.amenities.slice(0, 3).map((amenity, index) => (
                          <View key={index} style={styles.amenityBadge}>
                            <Text style={styles.amenityText}>{amenity}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                </LinearGradient>
              </TouchableOpacity>
            ))}
          </View>

          {/* Date & Time Scheduling */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Schedule Pickup</Text>
            
            <View style={styles.scheduleRow}>
              <TouchableOpacity
                style={styles.scheduleButton}
                onPress={() => setShowDatePicker(true)}
              >
                <Ionicons name="calendar" size={20} color="#D4AF37" />
                <Text style={styles.scheduleText}>{formatDate(scheduledDate)}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.scheduleButton}
                onPress={() => setShowTimePicker(true)}
              >
                <Ionicons name="time" size={20} color="#D4AF37" />
                <Text style={styles.scheduleText}>{formatTime(scheduledTime)}</Text>
              </TouchableOpacity>
            </View>

            {showDatePicker && (
              <DateTimePicker
                value={scheduledDate}
                mode="date"
                display="default"
                minimumDate={new Date()}
                onChange={(event, date) => {
                  setShowDatePicker(Platform.OS === 'ios');
                  if (date) setScheduledDate(date);
                }}
              />
            )}

            {showTimePicker && (
              <DateTimePicker
                value={scheduledTime}
                mode="time"
                display="default"
                onChange={(event, time) => {
                  setShowTimePicker(Platform.OS === 'ios');
                  if (time) setScheduledTime(time);
                }}
              />
            )}
          </View>

          {/* Additional Amenities */}
          {selectedVehicle && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Additional Amenities</Text>
              <View style={styles.amenitiesGrid}>
                {selectedVehicle.amenities.map((amenity, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.amenityChip,
                      selectedAmenities.includes(amenity) && styles.amenityChipSelected
                    ]}
                    onPress={() => toggleAmenity(amenity)}
                  >
                    <Text style={[
                      styles.amenityChipText,
                      selectedAmenities.includes(amenity) && styles.amenityChipTextSelected
                    ]}>
                      {amenity}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {/* Special Notes */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Special Requests (Optional)</Text>
            <TextInput
              style={styles.notesInput}
              placeholder="Any special requests or instructions..."
              placeholderTextColor="#666"
              value={notes}
              onChangeText={setNotes}
              multiline
              numberOfLines={3}
            />
          </View>

          {/* Fare Estimate */}
          <View style={styles.fareCard}>
            <LinearGradient
              colors={['#1e1e1e', '#2d2d2d']}
              style={styles.fareGradient}
            >
              <Text style={styles.fareLabel}>Estimated Fare</Text>
              <Text style={styles.fareAmount}>${getEstimatedFare().toFixed(2)}</Text>
              <Text style={styles.fareNote}>Final fare may vary based on actual distance</Text>
            </LinearGradient>
          </View>

          {/* Book Button */}
          <TouchableOpacity
            style={styles.bookButton}
            onPress={handleBooking}
            disabled={booking}
          >
            <LinearGradient
              colors={['#D4AF37', '#B8860B']}
              style={styles.bookGradient}
            >
              {booking ? (
                <ActivityIndicator color="#000" />
              ) : (
                <>
                  <Ionicons name="checkmark-circle" size={24} color="#000" />
                  <Text style={styles.bookButtonText}>Confirm Booking</Text>
                </>
              )}
            </LinearGradient>
          </TouchableOpacity>

          <View style={{ height: 30 }} />
        </ScrollView>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0a0a',
  },
  backgroundGradient: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0a0a0a',
  },
  loadingText: {
    color: '#fff',
    marginTop: 16,
    fontSize: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 50,
    paddingBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  locationCard: {
    backgroundColor: 'rgba(30,30,30,0.9)',
    borderRadius: 16,
    padding: 16,
    marginBottom: 24,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  locationText: {
    color: '#fff',
    fontSize: 14,
    marginLeft: 12,
    flex: 1,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 16,
  },
  vehicleCard: {
    borderRadius: 16,
    marginBottom: 16,
    overflow: 'hidden',
  },
  vehicleCardSelected: {
    borderWidth: 2,
    borderColor: '#D4AF37',
  },
  vehicleGradient: {
    padding: 16,
  },
  vehicleHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  vehicleInfo: {
    flex: 1,
    marginLeft: 12,
  },
  vehicleName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#fff',
  },
  vehicleNameSelected: {
    color: '#000',
  },
  vehicleDescription: {
    fontSize: 13,
    color: '#aaa',
    marginTop: 4,
  },
  vehicleDescriptionSelected: {
    color: 'rgba(0,0,0,0.7)',
  },
  vehiclePrice: {
    alignItems: 'flex-end',
  },
  priceText: {
    fontSize: 24,
    fontWeight: '800',
    color: '#D4AF37',
  },
  priceTextSelected: {
    color: '#000',
  },
  priceLabel: {
    fontSize: 12,
    color: '#888',
  },
  priceLabelSelected: {
    color: 'rgba(0,0,0,0.6)',
  },
  vehicleDetails: {
    flexDirection: 'row',
    marginTop: 12,
    gap: 16,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 13,
    color: '#aaa',
    marginLeft: 6,
  },
  detailTextSelected: {
    color: 'rgba(0,0,0,0.7)',
  },
  amenitiesPreview: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.2)',
  },
  amenitiesTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#000',
    marginBottom: 8,
  },
  amenitiesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  amenityBadge: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  amenityText: {
    fontSize: 11,
    color: '#000',
    fontWeight: '600',
  },
  scheduleRow: {
    flexDirection: 'row',
    gap: 12,
  },
  scheduleButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(30,30,30,0.9)',
    padding: 16,
    borderRadius: 12,
    gap: 12,
  },
  scheduleText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  amenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  amenityChip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: 'rgba(30,30,30,0.9)',
    borderWidth: 1,
    borderColor: 'rgba(212,175,55,0.3)',
  },
  amenityChipSelected: {
    backgroundColor: '#D4AF37',
    borderColor: '#D4AF37',
  },
  amenityChipText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  amenityChipTextSelected: {
    color: '#000',
  },
  notesInput: {
    backgroundColor: 'rgba(30,30,30,0.9)',
    borderRadius: 12,
    padding: 16,
    color: '#fff',
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  fareCard: {
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 20,
  },
  fareGradient: {
    padding: 20,
    alignItems: 'center',
  },
  fareLabel: {
    fontSize: 14,
    color: '#aaa',
    marginBottom: 8,
  },
  fareAmount: {
    fontSize: 36,
    fontWeight: '800',
    color: '#D4AF37',
    marginBottom: 8,
  },
  fareNote: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  bookButton: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  bookGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    gap: 12,
  },
  bookButtonText: {
    fontSize: 18,
    fontWeight: '800',
    color: '#000',
    letterSpacing: 1,
  },
});

export default BookLimousineScreen;

